import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", description: "", image: "", order: 0, active: true };

export default function AdminCategories() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const imgFileRef = useRef(null);

  const uploadImage = async (fileList) => {
    const file = Array.from(fileList || []).find((f) => f.type.startsWith("image/"));
    if (!file) { if ((fileList || []).length) toast.error("Only image files can be uploaded."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const onImageDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) uploadImage(e.dataTransfer.files);
  };

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => api.get("/categories", { params: { include_inactive: true } }).then((r) => r.data),
  });

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required."); return; }
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      setDialogOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/categories/${deleteId}`);
      toast.success("Category deleted");
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setDeleteId(null); }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-3xl text-slate-900">Categories</h1>
          <p className="text-slate-500">{categories.length} total</p>
        </div>
        <button onClick={openNew} data-testid="add-category-btn" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-blue/90">
          <Plus className="w-4 h-4" /> Add category
        </button>
      </div>

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((c) => (
            <div key={c.id} className="bg-white border rounded-xl overflow-hidden" data-testid={`category-item-${c.slug}`}>
              {c.image && <img src={c.image} alt={c.name} className="w-full h-32 object-cover border-b" />}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <h3 className="font-display font-bold text-slate-900">{c.name}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Hidden"}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</p>
                <p className="text-xs text-slate-400 mt-2">{c.product_count} products · order {c.order}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.slug}`} className="flex-1 inline-flex items-center justify-center gap-1 py-2 border rounded-lg text-sm font-medium hover:bg-slate-50"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => setDeleteId(c.id)} data-testid={`delete-category-${c.slug}`} className="px-3 py-2 border rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Name *</label>
              <input data-testid="cat-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Description</label>
              <textarea data-testid="cat-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Image</label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                onDrop={onImageDrop}
                data-testid="cat-image-dropzone"
                className={`rounded-lg border-2 border-dashed p-4 transition-colors ${dragActive ? "border-brand-blue bg-brand-blue/5" : "border-slate-300"}`}
              >
                {form.image && (
                  <div className="relative w-full h-28 mb-3 rounded-md overflow-hidden border">
                    <img src={form.image} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm({ ...form, image: "" })} data-testid="cat-image-remove" className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <input ref={imgFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => { uploadImage(e.target.files); e.target.value = ""; }} className="hidden" data-testid="cat-image-file" />
                  <button type="button" onClick={() => imgFileRef.current?.click()} disabled={uploading} data-testid="cat-upload-image" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg font-medium disabled:opacity-60">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload photo
                  </button>
                  <span className="text-xs text-slate-400">Drag &amp; drop or click · landscape ~1200×800 works best</span>
                </div>
                <input data-testid="cat-form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="…or paste an image URL" className="mt-3 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-sm font-semibold mb-1">Order</label>
                <input data-testid="cat-form-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer pb-2">
                <input data-testid="cat-form-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" /> Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 rounded-lg border font-medium">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-category-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-blue text-white font-semibold disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>Products in this category will remain but become uncategorised.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-category" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
